/**
 * Requirements Tree Navigator Component
 * Left panel showing hierarchical requirement tree with search and filters
 */
import React, { useState, useMemo } from 'react';
import { 
  Search, 
  Filter, 
  ChevronRight, 
  ChevronDown, 
  CheckCircle2, 
  AlertTriangle, 
  Circle,
  X
} from 'lucide-react';

export default function RequirementsTree({
  requirements,
  filteredRequirements,
  selectedRequirement,
  onSelectRequirement,
  searchQuery,
  onSearchChange,
  filters,
  onFiltersChange,
  coverage
}) {
  const [expandedNodes, setExpandedNodes] = useState(new Set(['req_1', 'req_2', 'req_3']));
  const [showFilters, setShowFilters] = useState(false);

  // Build tree structure
  const treeStructure = useMemo(() => {
    return buildTree(requirements);
  }, [requirements]);

  // Toggle node expansion
  const toggleNode = (id, e) => {
    e?.stopPropagation();
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedNodes(newExpanded);
  };

  // Check if any filters are active
  const hasActiveFilters = filters.status !== 'all' || filters.materiality !== 'all' || filters.mandatory !== 'all';

  // Clear all filters
  const clearFilters = () => {
    onFiltersChange({ status: 'all', materiality: 'all', mandatory: 'all' });
  };

  return (
    <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl overflow-hidden" data-testid="requirements-tree">
      {/* Search */}
      <div className="p-4 border-b border-[#1A1A1A]">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            placeholder="Search requirements..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none"
            data-testid="requirements-search"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-[#1A1A1A]">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-sm text-[#A0A0A0] hover:text-white transition-colors w-full"
        >
          <Filter size={14} />
          <span>Filters</span>
          {hasActiveFilters && (
            <span className="ml-auto px-1.5 py-0.5 text-[10px] bg-emerald-500/20 text-emerald-400 rounded">
              {Object.values(filters).filter(v => v !== 'all').length} active
            </span>
          )}
          <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>

        {showFilters && (
          <div className="mt-4 space-y-3">
            {/* Status Filter */}
            <div>
              <label className="text-xs font-medium text-[#6B6B6B] mb-1.5 block">Status</label>
              <select
                value={filters.status}
                onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
              >
                <option value="all">All Status</option>
                <option value="complete">Complete</option>
                <option value="partial">Partial</option>
                <option value="missing">Missing</option>
              </select>
            </div>

            {/* Materiality Filter */}
            <div>
              <label className="text-xs font-medium text-[#6B6B6B] mb-1.5 block">Materiality</label>
              <select
                value={filters.materiality}
                onChange={(e) => onFiltersChange({ ...filters, materiality: e.target.value })}
                className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
              >
                <option value="all">All</option>
                <option value="material">Material</option>
                <option value="not_material">Not Material</option>
              </select>
            </div>

            {/* Mandatory Filter */}
            <div>
              <label className="text-xs font-medium text-[#6B6B6B] mb-1.5 block">Type</label>
              <select
                value={filters.mandatory}
                onChange={(e) => onFiltersChange({ ...filters, mandatory: e.target.value })}
                className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
              >
                <option value="all">All Types</option>
                <option value="mandatory">Mandatory</option>
                <option value="conditional">Conditional</option>
              </select>
            </div>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-[#6B6B6B] hover:text-white transition-colors"
              >
                <X size={12} />
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Filter summary */}
        {hasActiveFilters && !showFilters && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-[#6B6B6B]">
              {filteredRequirements.length} of {requirements.length} requirements
            </span>
            <button
              onClick={clearFilters}
              className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Tree */}
      <div className="max-h-[500px] overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-[#2A2A2A] scrollbar-track-transparent">
        {treeStructure.map(node => (
          <TreeNode
            key={node.id}
            node={node}
            level={0}
            expandedNodes={expandedNodes}
            selectedRequirement={selectedRequirement}
            onToggle={toggleNode}
            onSelect={onSelectRequirement}
            coverage={coverage?.requirementsCoverage}
            filteredIds={new Set(filteredRequirements.map(r => r.id))}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="p-4 border-t border-[#1A1A1A] bg-[#0A0A0A]">
        <p className="text-xs font-medium text-[#6B6B6B] mb-2">Status Legend:</p>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="flex items-center gap-1.5">
            <CheckCircle2 size={12} className="text-emerald-400" />
            <span className="text-[#A0A0A0]">Complete</span>
          </div>
          <div className="flex items-center gap-1.5">
            <AlertTriangle size={12} className="text-amber-400" />
            <span className="text-[#A0A0A0]">Partial</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Circle size={12} className="text-[#4A4A4A]" />
            <span className="text-[#A0A0A0]">Missing</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Build tree from flat list
function buildTree(requirements) {
  const map = new Map();
  const roots = [];

  // Create map with children arrays
  requirements.forEach(req => {
    map.set(req.id, { ...req, children: [] });
  });

  // Build parent-child relationships
  requirements.forEach(req => {
    const node = map.get(req.id);
    if (req.parentRequirementId && map.has(req.parentRequirementId)) {
      map.get(req.parentRequirementId).children.push(node);
    } else if (req.requirementLevel === 0) {
      roots.push(node);
    }
  });

  return roots;
}

// Tree Node Component
function TreeNode({ 
  node, 
  level, 
  expandedNodes, 
  selectedRequirement, 
  onToggle, 
  onSelect, 
  coverage,
  filteredIds
}) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);
  const isSelected = selectedRequirement === node.id;
  const isFiltered = !filteredIds.has(node.id);
  const indent = level * 16;

  // Check if any children are in filtered set
  const hasFilteredChildren = hasChildren && node.children.some(child => 
    filteredIds.has(child.id) || (child.children && child.children.some(gc => filteredIds.has(gc.id)))
  );

  // Hide if filtered out and no filtered children
  if (isFiltered && !hasFilteredChildren && level > 0) return null;

  const nodeCoverage = coverage?.[node.id];
  
  const StatusIcon = nodeCoverage ? (
    nodeCoverage.status === 'complete' ? CheckCircle2 :
    nodeCoverage.status === 'partial' ? AlertTriangle : Circle
  ) : Circle;

  const statusColor = nodeCoverage ? (
    nodeCoverage.status === 'complete' ? 'text-emerald-400' :
    nodeCoverage.status === 'partial' ? 'text-amber-400' : 'text-[#4A4A4A]'
  ) : 'text-[#3A3A3A]';

  return (
    <div>
      <div
        className={`flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer transition-all group ${
          isSelected 
            ? 'bg-emerald-500/20 border border-emerald-500/30' 
            : 'hover:bg-[#1A1A1A] border border-transparent'
        } ${isFiltered ? 'opacity-40' : ''}`}
        style={{ paddingLeft: `${indent + 8}px` }}
        onClick={() => onSelect(node.id)}
        data-testid={`tree-node-${node.id}`}
      >
        {/* Expand/Collapse Icon */}
        {hasChildren ? (
          <button
            onClick={(e) => onToggle(node.id, e)}
            className="shrink-0 p-0.5 hover:bg-[#2A2A2A] rounded"
          >
            {isExpanded ? (
              <ChevronDown size={14} className="text-[#6B6B6B]" />
            ) : (
              <ChevronRight size={14} className="text-[#6B6B6B]" />
            )}
          </button>
        ) : (
          <div className="w-[18px] shrink-0" />
        )}

        {/* Status Icon */}
        <StatusIcon size={14} className={`shrink-0 ${statusColor}`} />

        {/* Code & Title */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-mono font-medium ${
              isSelected ? 'text-emerald-400' : 'text-[#808080]'
            }`}>
              {node.requirementCode}
            </span>
            {!node.isMandatory && (
              <span className="px-1 py-0.5 text-[9px] font-medium bg-[#1A1A1A] text-[#6B6B6B] rounded">
                Conditional
              </span>
            )}
          </div>
          <p className={`text-xs truncate ${
            isSelected ? 'text-white' : 'text-[#A0A0A0]'
          }`}>
            {node.requirementTitle}
          </p>
        </div>

        {/* Coverage indicator */}
        {nodeCoverage && nodeCoverage.status !== 'missing' && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
            nodeCoverage.status === 'complete' 
              ? 'bg-emerald-500/20 text-emerald-400' 
              : 'bg-amber-500/20 text-amber-400'
          }`}>
            {nodeCoverage.claimsCount || 0}/{nodeCoverage.evidenceCount || 0}
          </span>
        )}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {node.children.map(child => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              expandedNodes={expandedNodes}
              selectedRequirement={selectedRequirement}
              onToggle={onToggle}
              onSelect={onSelect}
              coverage={coverage}
              filteredIds={filteredIds}
            />
          ))}
        </div>
      )}
    </div>
  );
}
